import { z } from "zod";
import { variantSchema } from "./catalog-products.js";
import { defineEndpoint, idParamSchema, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** Prices: base price/cost/MRP, per-location selling prices, bulk edits and history (docs/specs/catalog.md, C-04). */
export const PRICE_FIELDS = ["price", "cost", "mrp"];
export const priceFieldSchema = z.enum(PRICE_FIELDS);
export const MAX_BULK_PRICE_VARIANTS = 500;
/** `PATCH /v1/variants/{id}/prices`: the tenant-wide prices (needs `prices.manage` at every location). */
export const updateVariantPricesRequestSchema = z
    .object({
    pricePaisa: paisa().min(0).optional(),
    costPaisa: paisa().min(0).optional(),
    mrpPaisa: paisa().min(0).nullable().optional(),
    version: z.int().min(1),
})
    .strict()
    .refine((change) => Object.keys(change).some((key) => key !== "version"), "Nothing to change");
export const locationPriceParamsSchema = z.object({ id: idParamSchema, locationId: idParamSchema });
/** `PUT /v1/variants/{id}/location-prices/{locationId}`: this location's selling price (≤ MRP). */
export const setLocationPriceRequestSchema = z.object({ pricePaisa: paisa().min(0) }).strict();
/**
 * `POST /v1/prices/bulk`: one field of many variants, set to an amount or adjusted by a percentage (+10 % = 1000,
 * −25 % = −2500). One transaction: all change or none.
 */
export const bulkPriceRequestSchema = z
    .object({
    variantIds: z
        .array(z.uuid())
        .min(1)
        .max(MAX_BULK_PRICE_VARIANTS)
        .refine((ids) => new Set(ids).size === ids.length, "Each variant once"),
    field: z.enum(["price", "cost"]),
    change: z.union([
        z.object({ setPaisa: paisa().min(0) }).strict(),
        z.object({ adjustBp: z.int().min(-10_000).max(10_000) }).strict(),
    ]),
})
    .strict();
export const bulkPriceResultSchema = z.object({
    /** Variants whose value actually changed (unchanged ones write no history). */
    changed: z.int(),
});
export const priceHistoryEntrySchema = z.object({
    id: z.uuid(),
    /** Null for the tenant-wide price; a location for its own selling price. */
    locationId: z.uuid().nullable(),
    field: priceFieldSchema,
    oldPaisa: paisa().nullable(),
    newPaisa: paisa().nullable(),
    changedBy: z.uuid().nullable(),
    changedAt: z.iso.datetime(),
});
export const priceHistorySchema = z.object({
    data: z.array(priceHistoryEntrySchema),
    nextCursor: z.uuid().nullable(),
});
export const priceHistoryQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
/** `catalog.price.changed`: one per changed value (search B-22, POS feed B-41). */
export const priceChangedEventSchema = z.object({
    productId: z.uuid(),
    variantId: z.uuid(),
    locationId: z.uuid().nullable(),
    field: priceFieldSchema,
    oldPaisa: paisa().nullable(),
    newPaisa: paisa().nullable(),
});
// Endpoints --------------------------------------------------------------------------------------------------
const catalog = { tag: "catalog" };
export const updateVariantPrices = defineEndpoint({
    ...catalog,
    name: "updateVariantPrices",
    method: "PATCH",
    path: "/v1/variants/{id}/prices",
    access: { permission: "prices.manage" },
    summary: "Change a variant's price, cost or MRP everywhere (optimistic version)",
    params: idParamsSchema,
    body: updateVariantPricesRequestSchema,
    status: 200,
    response: variantSchema,
    errors: ["CONFLICT", "PRICE_ABOVE_MRP"],
});
export const setLocationPrice = defineEndpoint({
    ...catalog,
    name: "setLocationPrice",
    method: "PUT",
    path: "/v1/variants/{id}/location-prices/{locationId}",
    access: { permission: "prices.manage", locationParam: "locationId" },
    summary: "Set this location's own selling price for a variant",
    params: locationPriceParamsSchema,
    body: setLocationPriceRequestSchema,
    status: 200,
    response: variantSchema,
    errors: ["PRICE_ABOVE_MRP"],
});
export const clearLocationPrice = defineEndpoint({
    ...catalog,
    name: "clearLocationPrice",
    method: "DELETE",
    path: "/v1/variants/{id}/location-prices/{locationId}",
    access: { permission: "prices.manage", locationParam: "locationId" },
    summary: "Go back to the tenant-wide price at this location",
    params: locationPriceParamsSchema,
    status: 200,
    response: variantSchema,
});
export const bulkUpdatePrices = defineEndpoint({
    ...catalog,
    name: "bulkUpdatePrices",
    method: "POST",
    path: "/v1/prices/bulk",
    access: { permission: "prices.manage" },
    summary: "Set or adjust the price or cost of up to 500 variants at once",
    body: bulkPriceRequestSchema,
    idempotent: true,
    status: 200,
    response: bulkPriceResultSchema,
    errors: ["PRICE_ABOVE_MRP"],
});
export const listPriceHistory = defineEndpoint({
    ...catalog,
    name: "listPriceHistory",
    method: "GET",
    path: "/v1/variants/{id}/price-history",
    access: { permission: "catalog.view" },
    summary: "Every price, cost and MRP change of a variant, newest first",
    params: idParamsSchema,
    query: priceHistoryQuerySchema,
    status: 200,
    response: priceHistorySchema,
});
//# sourceMappingURL=catalog-prices.js.map